import { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Spinner,
  Center,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
  Input,
  Select,
  Flex,
  Divider,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiFetchNotifications,
  apiMarkNotificationRead,
  NOTIFICATION_CATEGORIES,
} from 'api';
import { NotificationDetailModal } from 'components/notifications/NotificationDetailModal';
import Paginate from 'components/paginate/Paginate';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import type { Notification, APIPaginatedResponse } from 'types/api.types';

export const MyNotificationsView = () => {
  const isNotificationsEnabled = useFeatureFlag('enable-big-communications');
  const cardBg      = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const titleColor  = useColorModeValue('gray.800', 'white');
  const rowHoverBg  = useColorModeValue('gray.50', 'gray.700');
  const inputBg     = useColorModeValue('white', 'gray.700');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState<Notification | null>(null);

  // Filters
  const [search,   setSearch]   = useState('');
  const [category, setCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [allPage,    setAllPage]    = useState(0);
  const [unreadPage, setUnreadPage] = useState(0);

  const queryClient = useQueryClient();
  const filters = { search, category, dateFrom, dateTo };

  const { data: allData, isLoading: allLoading } = useQuery({
    queryKey: ['notifications', 'all', allPage, filters],
    queryFn: () => apiFetchNotifications(
      allPage + 1, 'all', 10,
      category || undefined, search || undefined,
      dateFrom || undefined, dateTo || undefined,
    ),
    enabled: isNotificationsEnabled,
  }) as { data: APIPaginatedResponse<Notification>; isLoading: boolean };

  const { data: unreadData, isLoading: unreadLoading } = useQuery({
    queryKey: ['notifications', 'unread', unreadPage, filters],
    queryFn: () => apiFetchNotifications(
      unreadPage + 1, 'unread', 10,
      category || undefined, search || undefined,
      dateFrom || undefined, dateTo || undefined,
    ),
    enabled: isNotificationsEnabled,
  }) as { data: APIPaginatedResponse<Notification>; isLoading: boolean };

  const markRead = useMutation({
    mutationFn: apiMarkNotificationRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  if (!isNotificationsEnabled) {
    return <Box p={6}><Text>This feature is not available.</Text></Box>;
  }

  const allNotifications: Notification[]    = allData?.results    ?? [];
  const allCount                            = allData?.count       ?? 0;
  const unreadNotifications: Notification[] = unreadData?.results ?? [];
  const unreadCount                         = unreadData?.count    ?? 0;

  const handleClick = (n: Notification) => {
    if (n.status === 'unread') markRead.mutate(n.id);
    setSelected(n);
    onOpen();
  };

  const resetFilters = () => {
    setSearch(''); setCategory(''); setDateFrom(''); setDateTo('');
    setAllPage(0); setUnreadPage(0);
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    const diff  = Date.now() - date.getTime();
    const days  = Math.floor(diff / 86400000);
    const hours = Math.floor(diff / 3600000);
    if (days  > 0) return `${days} day${days  > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderList = (
    items: Notification[], loading: boolean,
    count: number, page: number, setPage: (p: number) => void,
  ) => {
    if (loading) return <Center py={10}><Spinner /></Center>;
    if (!items.length) return (
      <Center py={10}><Text color={mutedColor}>No notifications</Text></Center>
    );
    return (
      <>
        <VStack spacing={0} align="stretch">
          {items.map((n, idx) => (
            <Box key={n.id}>
              <Box
                px={6}
                py={4}
                cursor="pointer"
                _hover={{ bg: rowHoverBg }}
                transition="background 0.12s"
                onClick={() => handleClick(n)}
              >
                <HStack justify="space-between" align="flex-start">
                  <VStack align="start" spacing={1} flex={1} mr={4}>
                    <Text
                      fontSize="sm"
                      fontWeight={n.status === 'unread' ? '700' : '600'}
                      color={titleColor}
                    >
                      {n.title}
                    </Text>
                    {n.summary && (
                      <Text fontSize="xs" color={mutedColor} noOfLines={2}>
                        {n.summary}
                      </Text>
                    )}
                    <Text fontSize="xs" color={mutedColor}>
                      {formatDate(n.created_at)}
                    </Text>
                  </VStack>
                  <HStack flexShrink={0} spacing={1}>
                    {n.is_edited && (
                      <Badge colorScheme="blue" fontSize="10px">EDITED</Badge>
                    )}
                    {n.requires_acknowledgement && (
                      <Badge colorScheme="red" fontSize="10px">⚠ MANDATORY</Badge>
                    )}
                  </HStack>
                </HStack>
              </Box>
              {idx < items.length - 1 && <Divider borderColor={borderColor} />}
            </Box>
          ))}
        </VStack>
        {count > 10 && (
          <Box px={6} py={4}>
            <Paginate page={page} count={count} onSetPage={setPage} pageSize={10} />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box p={6} maxW="900px" mx="auto">
      {/* Card */}
      <Box
        bg={cardBg}
        borderRadius="xl"
        border="1px solid"
        borderColor={borderColor}
        shadow="sm"
        overflow="hidden"
      >
        {/* ── Top row: card title + filters ── */}
        <Flex
          px={6}
          py={4}
          align="center"
          gap={3}
          flexWrap="wrap"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Text fontWeight="700" fontSize="md" color={titleColor} mr="auto">
            My Notifications
          </Text>

          {/* Keyword search */}
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setAllPage(0); setUnreadPage(0); }}
            size="sm"
            w="180px"
            bg={inputBg}
            borderRadius="md"
            fontSize="sm"
          />

          {/* Category dropdown */}
          <Select
            placeholder="Search Category..."
            value={category}
            onChange={(e) => { setCategory(e.target.value); setAllPage(0); setUnreadPage(0); }}
            size="sm"
            w="200px"
            bg={inputBg}
            borderRadius="md"
            fontSize="sm"
          >
            {NOTIFICATION_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </Select>

          {/* Date range */}
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="600" color={mutedColor} whiteSpace="nowrap">
              Date Range
            </Text>
            <Input
              type="date" value={dateFrom} size="sm" w="140px"
              bg={inputBg} borderRadius="md" fontSize="sm"
              onChange={(e) => { setDateFrom(e.target.value); setAllPage(0); setUnreadPage(0); }}
            />
            <Text fontSize="xs" color={mutedColor}>–</Text>
            <Input
              type="date" value={dateTo} size="sm" w="140px"
              bg={inputBg} borderRadius="md" fontSize="sm"
              onChange={(e) => { setDateTo(e.target.value); setAllPage(0); setUnreadPage(0); }}
            />
          </HStack>

          {(category || dateFrom || dateTo) && (
            <Text fontSize="xs" color="blue.500" cursor="pointer" textDecor="underline" onClick={resetFilters}>
              Clear
            </Text>
          )}
        </Flex>

        {/* ── Tabs ── */}
        <Tabs colorScheme="orange">
          <TabList px={6} borderBottom="1px solid" borderColor={borderColor}>
            <Tab fontSize="sm" fontWeight="600">All ({allCount})</Tab>
            <Tab fontSize="sm" fontWeight="600">Unread ({unreadCount})</Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0}>
              {renderList(allNotifications, allLoading, allCount, allPage, setAllPage)}
            </TabPanel>
            <TabPanel p={0}>
              {renderList(unreadNotifications, unreadLoading, unreadCount, unreadPage, setUnreadPage)}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <NotificationDetailModal isOpen={isOpen} onClose={onClose} notification={selected} />
    </Box>
  );
};
